import { useState, useEffect, useMemo } from 'react'
import { useParams, useRouter } from 'next/navigation'
import styled from 'styled-components'
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { fetchAPI } from '@/api'
import { MODALS } from '@/context'
import { useCountries, useModal, useToast, useTranslator } from '@/hooks'
import { type TypeLang } from '@/schema'
import { mq, vw, getP18, getRegularButton } from '@/styles'
import { Input, Select } from '../commons/inputs'
import { FileUpload } from '../commons/inputs/FileUpload'
import { autoComplete, LOCALE, LANGUAGES } from '@/utils'

const getTextOptions = (text) => {
  if(!text) return null
  return text.map(value => value)
}

// inquiries and trade values must match the array of values in the project.json -> project.modals.contact.json
type TypeFormInputs = {
  inquiry: 'project' | 'offer' | 'samples' | 'configurations' | 'control' | 'visit' | 'support' | 'showroom'
  first_name: string
  last_name: string
  email: string
  country: string
  message: string
  trade: 'architecture' | 'interior-design-studio' | 'lighting-design-studio' | 'engineering' | 'installation-company' | 'construction-company' | 'generalist-distributor' | 'specialist-distributor' | 'ecommerce-distributor' | 'chains' | 'others'
  company: string
  phone: number | string
  postal: string
  file?: File
}

export const FormContact = ({ inModal = false }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const { push } = useRouter()
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [defaultLocale, setDefaultLocale] = useState('')
  const countries = useCountries()
  const { toggle } = useModal(MODALS.CONTACT)
  const { t } = useTranslator()
  const { addToast } = useToast()

  const { register, handleSubmit, control, formState: { errors, isSubmitting }, setValue, reset } = useForm<TypeFormInputs>()

  const watchCountry = useWatch({ name: 'country', control })

  const inquiryOptions = useMemo(() => getTextOptions(t('project.modals.contact.inquiries')), [t])
  const tradeOptions = useMemo(() => getTextOptions(t('project.modals.contact.trades')), [t])

  const closeAndReset = () => {
    toggle()
    reset()
    setButtonDisabled(false)
  }

  const onSubmit: SubmitHandler<TypeFormInputs> = async data => {

    setButtonDisabled(true)
    const formData = new FormData()

    for (const [key, value] of Object.entries(data)) {
      if (key === 'inquiry' || key === 'trade') {
        const val = key === 'inquiry' ? inquiryOptions.find(option => option.value === value)?.name : tradeOptions.find(option => option.value === value)?.name
        formData.append(key, val)
      } else if (key !== 'file') {
        formData.append(key, value as string)
      } else {
        if(value?.[0]) formData.append(key, value?.[0])
      }
    }

    formData.append('locale', defaultLocale)

    const res = await fetchAPI('contact', { lang: lang }, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
      },
      body: formData
    }) as Response

    if (res.ok || res.status === 200) {
      if (window['dataLayer']) window['dataLayer'].push({ 'event': 'generate_lead', 'lead_type': 'contacto'})

      addToast({
        type: 'success',
        message: t('project.modals.contact.form_sent') as string,
        timeout: 5000
      })

      reset()
      setButtonDisabled(false)

      if (inModal) closeAndReset()
      push(`/${lang}/form-sent`)
    }

    setButtonDisabled(false)
  }

  useEffect(() => {
    const countrySelected = typeof watchCountry === 'string' ? watchCountry.toLowerCase() : LOCALE
    const langFound = LANGUAGES.find(langItem => langItem === countrySelected)
    queueMicrotask(() => setDefaultLocale(langFound ?? LOCALE))


  }, [watchCountry])


  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className='first'>
        <Select name='inquiry' register={register} placeholder={t('project.modals.contact.inquiry') as string} options={inquiryOptions} setFormValue={setValue} required={true} error={errors?.inquiry} autoComplete='off' placeholderVisible />
        <Input name='first_name' register={register} placeholder={t('form.fields.first_name') as string + '*'} error={errors.first_name} placeholderVisible autoComplete={autoComplete.name.firstName} required={{ required: t('form.errors.required') as string }} />
        <Input name='last_name' register={register} placeholder={t('form.fields.last_name') as string + '*'} error={errors.last_name} placeholderVisible autoComplete={autoComplete.name.lastName} required={{ required: t('form.errors.required') as string }} />
        <Input name='email' register={register} placeholder={t('form.fields.email') as string + '*'} error={errors.email} placeholderVisible autoComplete={autoComplete.email} required={{ required: t('form.errors.required') as string }} />
        {countries && <Select name='country' register={register} placeholder={t('project.modals.contact.country') as string} options={countries} setFormValue={setValue} required placeholderVisible />}
        <Input name='message' register={register} placeholder={t('project.modals.contact.message') as string} required={{ required: t('form.errors.empty') as string }} error={errors.message} autoComplete='off' />
      </div>
      <div className='second'>
        <Select name='trade' register={register} placeholder={t('project.modals.contact.trade') as string} options={tradeOptions} setFormValue={setValue} required={true} error={errors?.trade} autoComplete='off' placeholderVisible />
        <Input name='company' register={register} placeholder={t('project.modals.contact.company') as string} error={errors.company} placeholderVisible autoComplete={autoComplete.company.name} required={true} />
        <Input name='phone' register={register} placeholder={t('project.modals.contact.phone') as string} error={errors.phone} placeholderVisible autoComplete={autoComplete.tel.nationalNumber} required={true} />
        <Input name='postal' register={register} placeholder={t('project.modals.contact.postal') as string} required={{ required: t('form.errors.empty') as string}} error={errors.postal} placeholderVisible autoComplete={autoComplete.address.postalCode}/>
        <FileUpload name='file' register={register('file')} labelId='upload' label={t('project.modals.contact.file') as string} />
      </div>
      <button type='submit' className='submit' disabled={buttonDisabled}>
        {isSubmitting ? t('status.submitting') as string : t('project.modals.contact.button') as string}
      </button>
      <p dangerouslySetInnerHTML={{ __html: t('project.modals.contact.legal') as string}} className='legal' />
    </Form>
  )
}


const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  margin: 0 auto;
  max-width: 100%;
  padding: 0 30px;
  width: 100%;
  grid-column: 1 / span 6;

  ${mq.greaterThan('tablet')}{
    grid-column-gap: ${vw(25, 'desktop')};
    max-width: 600px;
    grid-template-columns: 1fr 1fr;
    grid-column: 1 / span 12;

  }

  ${mq.greaterThan('desktop-xs')}{
    max-width: ${vw(600, 'desktop')};
  }

  .title,
  .legal {
    text-align: center;
    margin: 0 auto;

    ${mq.greaterThan('tablet')}{
      max-width: ${vw(435, 'desktop')};
    }
  }

  > div {
    grid-column: 1 / span 2;

    ${mq.greaterThan('tablet')}{
      grid-column: auto;
    }
  }

  button[type='submit'],
  .legal {
    grid-column: 1 / span 2;
  }

  button[type='submit'] {
    ${getP18()}
    ${getRegularButton()}
    transition: background-color 300ms var(--transition-ease);
    margin: 30px auto;
    display: block;
    margin-bottom: ${vw(22, 'mobile')};

    ${mq.greaterThan('tablet')}{
      margin-bottom: ${vw(22, 'desktop')};
    }
  }

  input {
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-text-fill-color: var(--color-black);
      -webkit-box-shadow: 0 0 0 500px rgb(230, 227, 221) inset;
      box-shadow: 0 0 0 100px rgb(230, 227, 221) inset;
      color: inherit;
    }
  }

  input::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    opacity: 1;
  }
  input::-moz-placeholder { /* Firefox 19+ */
    opacity: 1;
  }
  input:-ms-input-placeholder { /* IE 10+ */
    opacity: 1;
  }
  input:-moz-placeholder { /* Firefox 18- */
    opacity: 1;
  }


  .first,
  .second {
    display: flex;
    flex-direction: column;
    gap: ${vw(55, 'mobile')};

    ${mq.greaterThan('tablet')}{
      gap: ${vw(60, 'desktop')};
    }
  }

  .second {
    margin-top: ${vw(55, 'mobile')};
    ${mq.greaterThan('tablet')}{
      margin-top: 0;
    }
  }
`
